import ApiService from "../../services/ApiService";
import translate from "../../services/Translate";

export const loginAs = (user_id: string) => () => {
  ApiService.get(`auth/login-as/${user_id}`)
    .then((response) => window.open(response.data.url, "_blank"))
    .catch((error) => console.error("loginAs", error));
};

export const logout = (user_id: string) => () => {
  ApiService.post(`admin/auth/logout-user?user_id=${user_id}`, {})
    .then((_) => alert("User has been logged out."))
    .catch((error) => console.error("logout", error));
};

export const displayDate = (value: string) => {
  try {
    return new Date(value).toISOString().slice(0, 16);
  } catch (_) {
    return String(value);
  }
};

export const fetchFlags = (setFlags: Function, showLoaded: Function) =>
  ApiService.get("flags")
    .then((response) => {
      setFlags(response.data.flags);
      showLoaded();
    })
    .catch((err) => console.error("An error occurred for fetching flags", err));

export const refreshUserLessons = (userId: string, setUserData: Function, setLoaded: Function) => {
  setLoaded((previousLoaded: any) => ({ ...previousLoaded, lessons: false }));

  ApiService.get(`users/${userId}/lessons`)
    .then((response) => {
      const userLessons = response.data.return;

      setUserData((previousUserData: any) => ({
        ...previousUserData,
        user_sprints: userLessons.sprints,
        user_modules: userLessons.user_modules,
      }));
    })
    .catch((error) => console.error("An error occurred while fetching user's lessons", error))
    .finally(() => setLoaded((previousLoaded: any) => ({ ...previousLoaded, lessons: true })));
};

const refreshUserResource = (
  userId: string,
  resource: "user" | "cohorts" | "projects" | "flags",
  setUserData: Function,
  setLoaded: Function
) => {
  const resourceConfig = {
    user: {
      path: `users/${userId}`,
      getData: (response: any) => response.data.user,
    },
    cohorts: {
      path: `users/${userId}/cohorts`,
      getData: (response: any) => ({ user_cohorts: response.data.cohorts }),
    },
    projects: {
      path: `users/${userId}/projects`,
      getData: (response: any) => ({ user_projects: response.data.projects }),
    },
    flags: {
      path: `users/${userId}/flags`,
      getData: (response: any) => ({ user_flags: response.data.user_flags }),
    },
  }[resource];

  setLoaded((previousLoaded: any) => ({ ...previousLoaded, [resource]: false }));

  ApiService.get(resourceConfig.path)
    .then((response) => {
      const resourceData = resourceConfig.getData(response);
      setUserData((previousUserData: any) => ({ ...previousUserData, ...resourceData }));
    })
    .catch((error) => console.error(`An error occurred while fetching user's ${resource}`, error))
    .finally(() => setLoaded((previousLoaded: any) => ({ ...previousLoaded, [resource]: true })));
};

export const refreshUserDetails = (userId: string, setUserData: Function, setLoaded: Function) =>
  refreshUserResource(userId, "user", setUserData, setLoaded);

export const refreshUserCohorts = (userId: string, setUserData: Function, setLoaded: Function) =>
  refreshUserResource(userId, "cohorts", setUserData, setLoaded);

export const refreshUserProjects = (userId: string, setUserData: Function, setLoaded: Function) =>
  refreshUserResource(userId, "projects", setUserData, setLoaded);

export const refreshUserFlags = (userId: string, setUserData: Function, setLoaded: Function) =>
  refreshUserResource(userId, "flags", setUserData, setLoaded);

export const updateUser = async (
  userData: any,
  editedUserData: any,
  setEditionLoading: Function,
  refreshUserBlock: Function,
  language: string,
  author_id?: number | null,
  removalReasonNote?: string
) => {
  let editions: any = {};
  setEditionLoading(true);

  Object.keys(userData).map((key: string) => {
    if (JSON.stringify(userData[key]) !== JSON.stringify(editedUserData[key])) {
      switch (key) {
        case "user_cohorts":
          editions.cohorts = {
            cohort_ids: [...editedUserData[key]].filter(Boolean).map((element) => element.id),
            ...(removalReasonNote?.trim() ? { removal_reason_note: removalReasonNote.trim() } : {}),
          };
          break;
        case "user_flags":
          editions.flags = {
            flag_ids: [...editedUserData[key]].map((element) => element.id),
            author_id,
          };
          break;
        default:
          editions.user = {
            ...editions.user,
            [key]: ["dt_start", "dt_end"].includes(key) ? ~~(editedUserData[key] / 1000) : editedUserData[key],
          };
          break;
      }
      return true;
    }
    return false;
  });

  Promise.all(
    Object.keys(editions).map((key_to_edit: string) => {
      switch (key_to_edit) {
        case "user":
          return ApiService.patch(`users/${userData.id}`, editions.user);
        case "cohorts":
          return ApiService.put(`users/${userData.id}/cohorts`, editions.cohorts);
        case "flags":
          return ApiService.put(`users/${userData.id}/flags`, editions.flags);
        default:
          break;
      }
      return true;
    })
  )
    .then((_) => refreshUserBlock(Object.keys(editions)))
    .catch((err) => {
      if (err.status === 409) {
        alert(translate(language, "PAGES.USER.USER_MODAL.ALERT.EMAIL_ALREADY_USED"));
      } else {
        alert(translate(language, "PAGES.USER.USER_MODAL.ALERT.GENERIC"));
      }
      console.error(err);
    })
    .finally(() => setEditionLoading(false));
};
